import React, { FC, useEffect } from 'react';
import { IPaymentConnector } from '~/modules/payment/synchrony/payment-connector';
import { TYPES } from '~/modules/payment/constants';
import CreditCard from '~/modules/payment/components/credit-card';
import Synchrony from '~/modules/payment/synchrony/components/synchrony';
import PaymentIframe from '~/modules/payment/components/payment-iframe';
import { ISalesOrderResponse } from '~/interfaces/get-sales-order';
import {
  AffirmConfiguration,
  ProgressiveConfiguration,
  SynchronyConfiguration,
  Maybe,
  UsAePayErrorMessage,
  FeatureFlag,
} from '~/gql/__generated__/types';
import ProgressiveForm from '~/modules/payment/progressive/components/progressive-form';
import { IGetPricing } from '~/interfaces/commerce-types';
import AffirmCheckout from '~/modules/payment/affirm';
import LearnHowSideDrawer from '../synchrony/components/checkout-synchrony-learn-how';
import {
  everydayFinancingOffersForCheckout,
  isThreePercentPromoEligible,
} from '~/modules/payment/synchrony/utils/get-financing-offer-details';
import { getProgressiveTotal } from '~/modules/payment/progressive/utils';
import { useOrderInprogress } from '~/hooks/use-order-inprogress';
import { IPaymentState } from '~/interfaces/payment';
import Adyen from './adyen';

interface IPaymentComponentProps {
  acceptContent: IPaymentConnector;
  checkoutCart: ISalesOrderResponse;
  checkoutConfigs: SynchronyConfiguration;
  progressiveOrderReview: (pricingInfo: IGetPricing) => void;
  cartId: string;
  isForterValidationEnabled: boolean;
  progressiveConfigs: ProgressiveConfiguration;
  affirmConfiguration: AffirmConfiguration;
  usaEPayErrorMessages?: Maybe<Maybe<UsAePayErrorMessage>[]> | undefined;
  paymentState?: IPaymentState;
  featureFlags?: Maybe<Maybe<FeatureFlag>[]> | undefined;
  isForterValidationEnabledForAdyen?: boolean;
}

export const PaymentOptions: FC<IPaymentComponentProps> = ({
  acceptContent,
  checkoutCart,
  checkoutConfigs,
  progressiveOrderReview,
  cartId,
  isForterValidationEnabled,
  progressiveConfigs,
  affirmConfiguration,
  usaEPayErrorMessages,
  paymentState,
  featureFlags,
  isForterValidationEnabledForAdyen,
}) => {
  const { setAcceptContent, setCartId } = useOrderInprogress();
  const selectedOffer = everydayFinancingOffersForCheckout(
    checkoutConfigs,
    checkoutCart.balanceAmount,
    true,
  )?.[0];
  const progressiveTotalAmount = getProgressiveTotal(
    checkoutCart,
    (progressiveConfigs?.progressive.progressiveStateDeliveryFee || []).filter(
      (state): state is string => !!state,
    ),
    false,
  );
  const amountDue = checkoutCart?.AmountDue ?? 0;
  const offerMonths = selectedOffer?.numOfMonths ?? 0;
  const minPurchase =
    checkoutConfigs?.financingOffers?.cashbackFinanceOffers?.[0]?.minPurchase ??
    0;
  const enable3PercentOffers =
    checkoutConfigs?.synchronyFinancingPurchaseRules?.enable3PercentOffers;

  const isExtraPromoEligible = isThreePercentPromoEligible(
    amountDue,
    minPurchase,
    enable3PercentOffers as number[],
    offerMonths,
  );
  const isProgressive = acceptContent.tenderType === TYPES.PROGRESIVE;
  const ctaType = isProgressive
    ? TYPES.PROGRESIVE.toLowerCase()
    : TYPES.AFFIRM.toLowerCase();
  const learnHowSideDrawer = (
    <LearnHowSideDrawer
      checkoutOffer={selectedOffer || {}}
      checkoutCart={checkoutCart}
      checkoutConfigs={checkoutConfigs}
      activeTab={isProgressive ? 2 : 1}
      progressiveTotalAmount={progressiveTotalAmount}
      ctaType={ctaType}
      isExtraPromoEligible={isExtraPromoEligible}
      paymentState={paymentState}
      featureFlags={featureFlags}
    />
  );

  useEffect(() => {
    if (acceptContent.tenderType === TYPES.AFFIRM) {
      setAcceptContent(acceptContent);
      setCartId(cartId);
    }
  }, [acceptContent, cartId, setAcceptContent, setCartId]);

  switch (acceptContent.tenderType) {
    case TYPES.CREDIT_CARD:
      return (
        <CreditCard
          acceptContent={acceptContent}
          isForterValidationEnabled={isForterValidationEnabled}
          cartId={cartId}
          orderDetails={checkoutCart}
          usaEPayErrorMessages={usaEPayErrorMessages}
          isForterValidationEnabledForAdyen={isForterValidationEnabledForAdyen}
        />
      );
    case TYPES.ADYEN:
      return (
        <Adyen
          acceptContent={acceptContent}
          isForterValidationEnabled={isForterValidationEnabled}
          cartId={cartId}
          orderDetails={checkoutCart}
          usaEPayErrorMessages={usaEPayErrorMessages}
          isForterValidationEnabledForAdyen={isForterValidationEnabledForAdyen}
        />
      );

    case TYPES.SYNCHRONY:
      return (
        <Synchrony
          acceptContent={acceptContent}
          checkoutCart={checkoutCart}
          checkoutConfigs={checkoutConfigs}
          progressiveConfigs={progressiveConfigs}
          paymentState={paymentState}
          featureFlags={featureFlags}
        />
      );
    case TYPES.PROGRESIVE:
      return (
        <ProgressiveForm
          checkoutCart={checkoutCart}
          progressiveOrderReview={(pricingInfo) =>
            progressiveOrderReview(pricingInfo)
          }
          progressiveConfigs={progressiveConfigs}
          cartId={cartId}
          learnHowSideDrawer={learnHowSideDrawer}
        />
      );
    case TYPES.AFFIRM:
      return (
        <>
          <AffirmCheckout
            acceptContent={acceptContent}
            affirmLeasingConfiguration={
              affirmConfiguration.affirmLeasingConfiguration
            }
            checkoutCart={checkoutCart}
            cartId={cartId}
          />
          {learnHowSideDrawer}
        </>
      );
    default:
      return (
        <PaymentIframe
          iframeAriaLabel="Payment Interface"
          displayContent={acceptContent.connectorHtml.AcceptPageContent}
          sourceUrl={acceptContent.connectorHtml.AcceptPageUrl}
          messageOrigin={acceptContent.connectorHtml.MessageOrigin}
          orderDetails={checkoutCart}
          paymentType=""
          cartId={cartId}
          isForterValidationEnabled={isForterValidationEnabled}
          usaEPayErrorMessages={usaEPayErrorMessages}
          isForterValidationEnabledForAdyen={isForterValidationEnabledForAdyen}
        />
      );
  }
};
